/** Back office → Academy · Sessions officielles (D-139): the official exam sessions of every country
 * and city, their check by a person before publication (« Vérifiée »), the CSV import, the assisted
 * search of the next sessions (AI, always « to verify ») and the « tell me » sign-ups.
 * Rights: examdates.manage (owner, manager, Academy manager, marketing). */

import {
  ExamEnum,
  type ExamSession,
  ExamSessionModeEnum,
  type ExamSessionRequest,
  LevelEnum,
  getExamDatesResearchesListQueryKey,
  useExamDatesAlertsList,
  useExamDatesMetaRetrieve,
  useExamDatesResearchesList,
  useExamDatesSessionsCreate,
  useExamDatesSessionsImportCreate,
  useExamDatesSessionsList,
  useExamDatesSessionsResearchCreate,
  useExamDatesSessionsUnverifyCreate,
  useExamDatesSessionsVerifyCreate,
} from "@agencyos/api-client";
import { sessionFee } from "@agencyos/core";
import { Badge, Button, EmptyState, GlassCard, GlassSheet, SelectField, Tabs, TextField, tabPanelProps } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarCheck2, ExternalLink, FileUp, Plus, Search } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../../components/AppShell";
import { fieldErrors } from "../../lib/settings";
import "./examDates.css";

type Tab = "sessions" | "researches" | "alerts";
const TABS: Tab[] = ["sessions", "researches", "alerts"];
const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const STATUS_TONE = { to_verify: "warning", verified: "success", past: "default" } as const;

/** A value of the form among the server's choices (the select only offers those). */
function pick<T extends string>(choices: readonly T[], value: string, fallback: T): T {
  return choices.find((choice) => choice === value) ?? fallback;
}

function useRefresh() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/exam-dates/") });
}

function useMeta(country = "") {
  const meta = useExamDatesMetaRetrieve(country ? { country } : undefined, { query: { staleTime: 600_000 } });
  return meta.data?.status === 200 ? meta.data.data : undefined;
}

function dayText(value: string | null | undefined, language: string): string {
  if (!value) return "";
  return new Date(`${value}T12:00:00`).toLocaleDateString(language === "fr" ? "fr-FR" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function SessionRow({ row }: { row: ExamSession }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefresh();
  const verify = useExamDatesSessionsVerifyCreate({ mutation: { onSuccess: refresh } });
  const unverify = useExamDatesSessionsUnverifyCreate({ mutation: { onSuccess: refresh } });
  const fee = sessionFee({ fee_amount: row.fee_amount ?? null, fee_currency: row.fee_currency ?? "" });
  return (
    <GlassCard as="article" className="ed-row" aria-labelledby={`es-${row.id}`}>
      <div className="ed-row__head">
        <h3 id={`es-${row.id}`}>
          {t(`examDates.exam.${row.exam}`)}
          {row.level ? ` ${row.level}` : ""} · {row.city} ({row.country})
        </h3>
        <Badge tone={STATUS_TONE[row.status]}>{t(`examDates.status.${row.status}`)}</Badge>
      </div>
      <p className="muted">
        {row.center_name}
        {row.center_known ? "" : ` · ${t("examDates.centerUnknown")}`}
      </p>
      <dl className="ed-facts">
        <div>
          <dt>{t("examDates.fields.dates")}</dt>
          <dd>{row.dates.map((d) => dayText(d, i18n.language)).join(" · ")}</dd>
        </div>
        <div>
          <dt>{t("examDates.fields.deadline")}</dt>
          <dd>{dayText(row.registration_deadline, i18n.language) || t("examDates.notGiven")}</dd>
        </div>
        {fee ? (
          <div>
            <dt>{t("examDates.fields.fee")}</dt>
            <dd>
              {fee} · {t("examDates.noted", { date: dayText(row.fee_noted_on, i18n.language) })}
            </dd>
          </div>
        ) : null}
        <div>
          <dt>{t("examDates.fields.source")}</dt>
          <dd>
            <a href={row.source_url} target="_blank" rel="noopener noreferrer">
              {new URL(row.source_url).hostname} <ExternalLink size={12} aria-hidden="true" />
            </a>{" "}
            · {t("examDates.readOn", { date: dayText(row.source_checked_on, i18n.language) })}
          </dd>
        </div>
      </dl>
      {row.source_quote ? <blockquote className="ed-quote">{row.source_quote}</blockquote> : null}
      {row.notes ? <p className="muted">{row.notes}</p> : null}
      <div className="row gap">
        {row.status === "to_verify" ? (
          <Button size="sm" icon={<CalendarCheck2 size={16} />} loading={verify.isPending} onClick={() => verify.mutate({ id: row.id })}>
            {t("examDates.verify")}
          </Button>
        ) : null}
        {row.status === "verified" ? (
          <Button size="sm" variant="secondary" loading={unverify.isPending} onClick={() => unverify.mutate({ id: row.id })}>
            {t("examDates.unverify")}
          </Button>
        ) : null}
        <a className="ed-link" href={row.registration_url} target="_blank" rel="noopener noreferrer">
          {t("examDates.registrationLink")}
        </a>
      </div>
      {row.status === "to_verify" ? <p className="muted ed-hint">{t("examDates.verifyHint")}</p> : null}
    </GlassCard>
  );
}

function SessionForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefresh();
  const [values, setValues] = useState({ exam: "tcf_canada", level: "", country: "CM", city: "", center_name: "", dates: "", registration_deadline: "", mode: "", fee_amount: "", fee_currency: "", registration_url: "", source_url: "", notes: "" });
  const meta = useMeta(values.country);
  const create = useExamDatesSessionsCreate({ mutation: { onSuccess: () => { refresh(); onDone(); } } });
  const errors = fieldErrors(create.error);
  const set = (key: keyof typeof values) => (event: { target: { value: string } }) => setValues({ ...values, [key]: event.target.value });
  const cities = (meta?.centers?.[values.exam] ?? []).map((c) => String(c.city ?? ""));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const exam = pick(Object.values(ExamEnum), values.exam, ExamEnum.tcf_canada);
    const data: ExamSessionRequest = {
      exam,
      level: exam === "goethe" ? pick<"" | LevelEnum>(["", ...Object.values(LevelEnum)], values.level, "") : "",
      mode: pick<"" | ExamSessionModeEnum>(["", ...Object.values(ExamSessionModeEnum)], values.mode, ""),
      country: values.country,
      city: values.city,
      center_name: values.center_name,
      dates: values.dates.split(/[\s,;|]+/).filter(Boolean),
      registration_deadline: values.registration_deadline || null,
      fee_amount: values.fee_amount || null,
      fee_currency: values.fee_currency,
      registration_url: values.registration_url,
      source_url: values.source_url,
      notes: values.notes,
    };
    create.mutate({ data });
  };
  return (
    <form className="stack" onSubmit={submit}>
      <div className="ed-grid">
        <SelectField label={t("examDates.fields.exam")} value={values.exam} onChange={set("exam")} options={(meta?.exams ?? []).map((e) => ({ value: String(e.code), label: String(e.name) }))} />
        {values.exam === "goethe" ? <SelectField label={t("examDates.fields.level")} value={values.level} onChange={set("level")} error={errors.level} options={[{ value: "", label: "—" }, ...LEVELS.map((l) => ({ value: l, label: l }))]} /> : null}
        <SelectField label={t("examDates.fields.country")} value={values.country} onChange={set("country")} options={(meta?.countries ?? []).map((c) => ({ value: String(c.code), label: String(c.name) }))} />
        <TextField label={t("examDates.fields.city")} value={values.city} onChange={set("city")} error={errors.city} list="ed-cities" required />
        <datalist id="ed-cities">{cities.map((c) => <option key={c} value={c} />)}</datalist>
        <TextField label={t("examDates.fields.center")} value={values.center_name} onChange={set("center_name")} error={errors.center_name} required />
        <TextField label={t("examDates.fields.dates")} hint={t("examDates.datesHint")} value={values.dates} onChange={set("dates")} error={errors.dates} required />
        <TextField type="date" label={t("examDates.fields.deadline")} value={values.registration_deadline} onChange={set("registration_deadline")} error={errors.registration_deadline} />
        <SelectField label={t("examDates.fields.mode")} value={values.mode} onChange={set("mode")} options={[{ value: "", label: t("examDates.mode.unknown") }, { value: "computer", label: t("examDates.mode.computer") }, { value: "paper", label: t("examDates.mode.paper") }]} />
        <TextField label={t("examDates.fields.fee")} hint={t("examDates.feeHint")} inputMode="decimal" value={values.fee_amount} onChange={set("fee_amount")} error={errors.fee_amount} />
        <TextField label={t("examDates.fields.currency")} maxLength={3} value={values.fee_currency} onChange={set("fee_currency")} error={errors.fee_currency} />
        <TextField type="url" label={t("examDates.fields.registrationUrl")} value={values.registration_url} onChange={set("registration_url")} error={errors.registration_url} required />
        <TextField type="url" label={t("examDates.fields.source")} hint={t("examDates.sourceHint")} value={values.source_url} onChange={set("source_url")} error={errors.source_url} required />
      </div>
      <TextField label={t("examDates.fields.notes")} value={values.notes} onChange={set("notes")} />
      {create.isError && !Object.keys(errors).length ? <p className="ko">{t("common.error")}</p> : null}
      <Button type="submit" loading={create.isPending}>{t("examDates.save")}</Button>
    </form>
  );
}

function ResearchBox() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const meta = useMeta();
  const [exam, setExam] = useState("");
  const [country, setCountry] = useState("CM");
  const research = useExamDatesSessionsResearchCreate({
    mutation: { onSuccess: () => void queryClient.invalidateQueries({ queryKey: getExamDatesResearchesListQueryKey() }) },
  });
  return (
    <GlassCard as="section" className="stack" aria-labelledby="ed-research">
      <h2 id="ed-research">{t("examDates.research.title")}</h2>
      <p className="muted">{t("examDates.research.help")}</p>
      <div className="ed-grid">
        <SelectField label={t("examDates.fields.exam")} value={exam} onChange={(e) => setExam(e.target.value)} options={[{ value: "", label: t("examDates.research.allExams") }, ...(meta?.exams ?? []).map((e) => ({ value: String(e.code), label: String(e.name) }))]} />
        <SelectField label={t("examDates.fields.country")} value={country} onChange={(e) => setCountry(e.target.value)} options={(meta?.countries ?? []).map((c) => ({ value: String(c.code), label: String(c.name) }))} />
      </div>
      <div className="row gap">
        <Button icon={<Search size={16} />} loading={research.isPending} onClick={() => research.mutate({ data: { exam: pick<"" | ExamEnum>(["", ...Object.values(ExamEnum)], exam, ""), country } })}>
          {t("examDates.research.run")}
        </Button>
        {research.isSuccess ? <span role="status" className="muted">{t("examDates.research.queued")}</span> : null}
      </div>
    </GlassCard>
  );
}

function ImportBox() {
  const { t } = useTranslation();
  const refresh = useRefresh();
  const meta = useMeta();
  const upload = useExamDatesSessionsImportCreate({ mutation: { onSuccess: refresh } });
  const result = upload.data?.status === 200 ? upload.data.data : undefined;
  return (
    <GlassCard as="section" className="stack" aria-labelledby="ed-import">
      <h2 id="ed-import">{t("examDates.import.title")}</h2>
      <p className="muted">{t("examDates.import.help", { columns: (meta?.csv_columns ?? []).join(", ") })}</p>
      <label className="ed-file">
        <FileUp size={16} aria-hidden="true" /> {t("examDates.import.choose")}
        <input type="file" accept=".csv,text/csv" onChange={(e) => { const file = e.target.files?.[0]; if (file) upload.mutate({ data: { file } }); }} />
      </label>
      {result ? (
        <p role="status">
          {t("examDates.import.result", { created: result.created, duplicates: result.duplicates, errors: result.errors.length })}
        </p>
      ) : null}
      {result?.errors.length ? (
        <ul className="ed-errors">
          {result.errors.slice(0, 20).map((e, i) => (
            <li key={i}>{t("examDates.import.line", { line: String(e.line ?? "") })} : {JSON.stringify(e.errors)}</li>
          ))}
        </ul>
      ) : null}
    </GlassCard>
  );
}

function SessionsTab() {
  const { t } = useTranslation();
  const meta = useMeta();
  const [filters, setFilters] = useState({ exam: "", country: "", status: "to_verify", upcoming: true });
  const [adding, setAdding] = useState(false);
  const list = useExamDatesSessionsList({ ...filters, exam: filters.exam || undefined, country: filters.country || undefined, status: filters.status || undefined });
  const rows = list.data?.status === 200 ? list.data.data.results : [];
  return (
    <div className="stack">
      <GlassCard className="ed-filters">
        <SelectField label={t("examDates.fields.exam")} value={filters.exam} onChange={(e) => setFilters({ ...filters, exam: e.target.value })} options={[{ value: "", label: t("examDates.all") }, ...(meta?.exams ?? []).map((e) => ({ value: String(e.code), label: String(e.name) }))]} />
        <SelectField label={t("examDates.fields.country")} value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} options={[{ value: "", label: t("examDates.all") }, ...(meta?.countries ?? []).map((c) => ({ value: String(c.code), label: String(c.name) }))]} />
        <SelectField label={t("examDates.fields.status")} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} options={[{ value: "", label: t("examDates.all") }, ...(["to_verify", "verified", "past"] as const).map((s) => ({ value: s, label: t(`examDates.status.${s}`) }))]} />
        <label className="ed-check">
          <input type="checkbox" checked={filters.upcoming} onChange={(e) => setFilters({ ...filters, upcoming: e.target.checked })} /> {t("examDates.upcomingOnly")}
        </label>
        <Button icon={<Plus size={16} />} onClick={() => setAdding(true)}>{t("examDates.add")}</Button>
      </GlassCard>
      {list.isLoading ? <p className="muted">{t("common.loading")}</p> : null}
      {!list.isLoading && !rows.length ? <EmptyState icon={<CalendarCheck2 size={22} strokeWidth={1.75} />} title={t("examDates.empty")} description={t("examDates.emptyHelp")} /> : null}
      <div className="ed-list">{rows.map((row) => <SessionRow key={row.id} row={row} />)}</div>
      <div className="ed-side">
        <ResearchBox />
        <ImportBox />
      </div>
      <GlassSheet open={adding} title={t("examDates.add")} onClose={() => setAdding(false)}>
        <SessionForm onDone={() => setAdding(false)} />
      </GlassSheet>
    </div>
  );
}

function ResearchesTab() {
  const { t, i18n } = useTranslation();
  const list = useExamDatesResearchesList(undefined, { query: { refetchInterval: 10_000 } });
  const rows = list.data?.status === 200 ? list.data.data.results : [];
  if (!rows.length) return <EmptyState title={t("examDates.research.none")} description={t("examDates.research.help")} />;
  return (
    <div className="ed-list">
      {rows.map((r) => (
        <GlassCard as="article" key={r.id} className="stack">
          <div className="ed-row__head">
            <h3>{(r.exam ? t(`examDates.exam.${r.exam}`) : t("examDates.research.allExams")) + ` · ${r.country}`}</h3>
            <Badge tone={r.status === "done" ? "success" : r.status === "failed" ? "danger" : "info"}>{t(`examDates.research.status.${r.status}`)}</Badge>
          </div>
          <p className="muted">{t("examDates.research.counts", { found: r.found, created: r.created, rejected: Array.isArray(r.rejected) ? r.rejected.length : 0 })} · {dayText(r.created_at.slice(0, 10), i18n.language)}</p>
          {r.error ? <p className="ko">{r.error}</p> : null}
          {r.notes ? <p className="muted ed-pre">{r.notes}</p> : null}
        </GlassCard>
      ))}
    </div>
  );
}

function AlertsTab() {
  const { t, i18n } = useTranslation();
  const list = useExamDatesAlertsList();
  const rows = list.data?.status === 200 ? list.data.data.results : [];
  if (!rows.length) return <EmptyState title={t("examDates.alerts.none")} description={t("examDates.alerts.help")} />;
  return (
    <GlassCard>
      <table className="ed-table">
        <thead>
          <tr>
            <th>{t("examDates.alerts.email")}</th>
            <th>{t("examDates.fields.exam")}</th>
            <th>{t("examDates.alerts.place")}</th>
            <th>{t("examDates.alerts.since")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id}>
              <td>{a.email}{a.unsubscribed_at ? ` · ${t("examDates.alerts.unsubscribed")}` : ""}</td>
              <td>{t(`examDates.exam.${a.exam}`)}{a.level ? ` ${a.level}` : ""}</td>
              <td>{a.city ? `${a.city}, ` : ""}{a.country}</td>
              <td>{dayText(a.consent_at.slice(0, 10), i18n.language)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </GlassCard>
  );
}

export function ExamDatesPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("sessions");
  return (
    <Page eyebrow={t("examDates.eyebrow")} title={t("examDates.title")} description={t("examDates.description")}>
      <Tabs label={t("examDates.title")} idPrefix="ed" value={tab} onChange={setTab} items={TABS.map((v) => ({ value: v, label: t(`examDates.tabs.${v}`) }))} />
      <section {...tabPanelProps("ed", tab)}>
        {tab === "sessions" ? <SessionsTab /> : tab === "researches" ? <ResearchesTab /> : <AlertsTab />}
      </section>
    </Page>
  );
}
